import { Link } from "react-router-dom";
import { Reveal } from "@/lib/motion";
import { MonitorSmartphone, Search, BarChart3, Bot, ShieldCheck, ArrowRight } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

const ITEMS = [
  {
    icon: MonitorSmartphone,
    title: "AI-Powered Websites",
    desc: "Intelligent sites that answer questions, guide visitors and qualify leads around the clock.",
    article: "Modern websites should do more than display information. WebsiteTech.pro builds intelligent, AI-powered websites that can answer questions, guide visitors, recommend services, capture and qualify leads, and create more personalized experiences. From AI assistants and intelligent search to multilingual interaction and dynamic content, we transform traditional websites into active digital platforms that work for your business around the clock.",
    cta: "Explore AI-Powered Websites",
    to: "/build/ai-website",
  },
  {
    icon: Search,
    title: "SEO & Search Visibility",
    desc: "Technical SEO, structured data and AI-search readiness so the right people find you.",
    article: "A great website delivers little value if the right people cannot find it. WebsiteTech combines technical SEO, content strategy, structured data, local search optimization, internal linking, performance, and AI-search readiness to improve how your business is discovered. We build websites so both people and search technologies can clearly understand who you are, what you offer, and why your information is relevant.",
    cta: "Improve My Search Visibility",
    to: "/improve/seo",
  },
  {
    icon: BarChart3,
    title: "Analytics That Matter",
    desc: "Understand what actually generates leads and sales — not just page views.",
    article: "Website traffic is only the beginning. WebsiteTech helps you understand where visitors come from, what they are looking for, which pages engage them, where they leave, and what actually generates leads and sales. AI-assisted analytics can turn complicated website data into understandable insights, recommendations, and priorities \u2014 helping you make decisions based on evidence rather than guesswork.",
    cta: "Explore Website Analytics",
    to: "/analytics/website-analytics",
  },
  {
    icon: Bot,
    title: "AI & Automation",
    desc: "Qualify leads, route inquiries and trigger workflows so your team does less manual work.",
    article: "Your website can become an active part of your business operations. WebsiteTech combines AI with intelligent automation to qualify leads, route inquiries, schedule appointments, trigger communications, update business systems, assist customers, and automate repetitive workflows. The objective is simple: reduce manual work, respond faster, improve customer experiences, and give your team more time for work that requires human expertise.",
    cta: "Discover What You Can Automate",
    to: "/ai/automation",
  },
  {
    icon: ShieldCheck,
    title: "Security & Performance",
    desc: "Fast, secure, reliable websites that build trust and support stronger conversions.",
    article: "Visitors expect websites to be fast, secure, reliable, and easy to use on every device. WebsiteTech builds and modernizes websites with performance, mobile experience, HTTPS, secure authentication, backups, monitoring, optimized images, efficient code, and other appropriate safeguards in mind. A faster and more dependable website improves customer confidence and can also support stronger search performance and conversions.",
    cta: "Check My Website Health",
    to: "/improve/website-audit",
  },
];

export default function Capabilities() {
  return (
    <section className="relative z-10 py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="overline">What We Do</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mt-3">
            Everything Your Website Needs to Work Smarter
          </h2>
          <p className="text-slate-500 text-base md:text-lg mt-4 leading-relaxed">
            One technology partner for AI, search, analytics, automation, performance and continuous improvement.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mt-14">
          {ITEMS.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.08} className="h-full">
              <HoverCard openDelay={90} closeDelay={80}>
                <HoverCardTrigger asChild>
                  <Link
                    to={it.to}
                    data-testid={`capability-${i}`}
                    className="group text-center px-4 py-6 h-full flex flex-col items-center rounded-2xl border border-transparent hover:border-slate-200 hover:soft-shadow transition-all duration-300"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-brand group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                      <it.icon size={28} />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-slate-900 mt-5">{it.title}</h3>
                    <p className="text-slate-500 text-sm mt-2.5 leading-relaxed flex-1">{it.desc}</p>
                    <span className="inline-flex items-center gap-1 text-brand text-sm font-semibold mt-4 group-hover:gap-2 transition-all">
                      {it.cta} <ArrowRight size={14} />
                    </span>
                  </Link>
                </HoverCardTrigger>
                <HoverCardContent className="w-80" data-testid={`capability-article-${i}`}>
                  <h4 className="font-heading text-base font-bold text-slate-900">{it.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">{it.article}</p>
                  <Link to={it.to} className="inline-flex items-center gap-1.5 text-brand text-sm font-semibold mt-3 hover:gap-2.5 transition-all">
                    {it.cta} <ArrowRight size={14} />
                  </Link>
                </HoverCardContent>
              </HoverCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
